/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';

import template from './cdn-upload-edit-page.component.html';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import type {FileUploadHandler} from '../../common/directives/file-upload/file-upload.util';
import type {FileUploadState} from '../../common/directives/file-upload/file-upload.component';
import {CdnUploadApiService, CdnUploadModel} from './cdn-upload-api.service';
import {AppSharedModule} from '../../common/app-shared.module';
import {CreativeAssetsModule} from '../../creative/creative-assets.module';

const UPLOAD_ACCEPT = '.webp,.jpg,.png,.jpeg,.gif,.zip,.swf,.html,.htm,.js,.txt,.json,.css,.xhtml,.xml,.woff,.eot,.svg,.ttf,.woff,.woff2,.htc,.mp4,.mp3,.webm,.vtt,.srt';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, CreativeAssetsModule],
  selector: 'adn-cdn-upload-edit-page',
  template
})
export class CdnUploadEditPageComponent extends SignalAdminResourceEditPageBase<CdnUploadModel> {
  readonly uploadAccept = UPLOAD_ACCEPT;
  readonly cdnUploadWarningOptions = {mode: 'cdn-asset' as const};
  readonly newUploadState = signal<FileUploadState>({
    pristine: true,
    pending: false,
    invalid: false
  });

  constructor(private readonly cdnApi: CdnUploadApiService) {
    super(cdnApi, 'cdnAsset');
  }

  uploadCdnFile: FileUploadHandler = (file, onProgress) => {
    return this.cdnApi.upload(this.editingModel.id, file, onProgress);
  };

  httpsPrefix(value: string): string {
    return value && value.indexOf('//') === 0 ? 'https:' + value : value;
  }

  onAssetUploaded(asset: CdnUploadModel): void {
    this.saveDirect(asset);
  }

  onUploadStateChange(state: FileUploadState): void {
    this.newUploadState.set(state);
  }

  showsAssetDetails(): boolean {
    return !!(this.editingModel?.cdnId || this.editingModel?.fileName || this.editingModel?.mimeType);
  }

  isActionDisabled(editForm: any): boolean {
    if (!this.showsAssetDetails()) {
      const uploadState = this.newUploadState();
      return uploadState.pristine
        || uploadState.pending
        || (uploadState.invalid && !this.hasValidationError);
    }
    return this.isSaveDisabled(editForm);
  }
}
